import { useState, type ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import { useI18n } from '../../lib/i18n/I18nProvider';
import { newIntent, useApiAction, useApiList } from '../../lib/api/queries';
import type { Sponsor, SponsorTier } from '../../lib/api/types';
import { Badge, Card, DataTable, Field, Ltr, PageTitle, Select } from '../../ui/primitives';
import { EmptyState, ErrorState, LoadingState } from '../../ui/states';
import { RefusalWithRemedy } from '../../ui/guards';
import { statusTone } from './Expos';

/**
 * `ADM-39` — the Expo Website Sponsorship surface.
 *
 * The five default tiers are OFFERED as a starting point and never created silently: the operator
 * sees them as a form pre-fill and creates each one deliberately.
 */
const DEFAULT_TIERS = [
  { code: 'main', name_ar: 'الراعي الرئيسي', name_en: 'Main Sponsor', rank: 1 },
  { code: 'platinum', name_ar: 'بلاتيني', name_en: 'Platinum', rank: 2 },
  { code: 'gold', name_ar: 'ذهبي', name_en: 'Gold', rank: 3 },
  { code: 'silver', name_ar: 'فضي', name_en: 'Silver', rank: 4 },
  { code: 'partner', name_ar: 'شريك', name_en: 'Partner', rank: 5 },
];

export function AdminSponsors(): ReactElement {
  const { expoId = '' } = useParams();
  const { t, bilingual, formatDateTime } = useI18n();
  const tiersPath = `/v1/admin/expos/${expoId}/sponsor-tiers`;
  const sponsorsPath = `/v1/admin/expos/${expoId}/sponsors`;
  const tiers = useApiList<SponsorTier>(tiersPath);
  const sponsors = useApiList<Sponsor>(sponsorsPath);
  const createTier = useApiAction<SponsorTier>([tiersPath]);
  const createSponsor = useApiAction<Sponsor>([sponsorsPath]);
  const sponsorAction = useApiAction<Sponsor>([sponsorsPath]);

  const [tierCode, setTierCode] = useState('');
  const [tierAr, setTierAr] = useState('');
  const [tierEn, setTierEn] = useState('');
  const [tierRank, setTierRank] = useState('1');

  const [sponsorName, setSponsorName] = useState('');
  const [tierId, setTierId] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [order, setOrder] = useState('1');
  const [activeFrom, setActiveFrom] = useState('');
  const [activeTo, setActiveTo] = useState('');

  const tierItems = tiers.data?.items ?? [];

  return (
    <>
      <PageTitle title={t('admin.expo.sponsors')}>{t('admin.sponsors.desc')}</PageTitle>

      <Card title="sponsor tiers">
        {tiers.isLoading ? <LoadingState /> : null}
        {tiers.error ? <ErrorState error={tiers.error} onRetry={() => void tiers.refetch()} /> : null}
        {tiers.data ? (
          <DataTable<SponsorTier>
            caption="sponsor tiers"
            rows={tierItems}
            rowKey={(x) => x.tier_id}
            empty={
              <EmptyState>
                <p>{t('admin.sponsors.desc')}</p>
                <div className="row">
                  {DEFAULT_TIERS.map((d) => (
                    <button
                      key={d.code}
                      type="button"
                      className="btn btn--secondary btn--small"
                      onClick={() => {
                        setTierCode(d.code);
                        setTierAr(d.name_ar);
                        setTierEn(d.name_en);
                        setTierRank(String(d.rank));
                      }}
                    >
                      {d.name_en}
                    </button>
                  ))}
                </div>
              </EmptyState>
            }
            columns={[
              { key: 'code', header: t('table.code'), cell: (x) => <Ltr>{x.code}</Ltr> },
              { key: 'name', header: t('table.name'), cell: (x) => bilingual(x.name_ar, x.name_en) },
              { key: 'rank', header: t('table.rank'), cell: (x) => <span className="numeral">{x.rank}</span> },
              {
                key: 'max',
                header: 'max_sponsors',
                cell: (x) => (x.max_sponsors === undefined ? t('table.none') : String(x.max_sponsors)),
              },
            ]}
          />
        ) : null}

        <form
          onSubmit={(ev) => {
            ev.preventDefault();
            createTier.mutate(newIntent({
              path: tiersPath,
              body: {
                code: tierCode,
                name_ar: tierAr,
                name_en: tierEn,
                rank: Number(tierRank),
                placements: ['website_strip'],
              },
            }));
          }}
        >
          <Field label="code" name="tier-code" value={tierCode} onChange={setTierCode} required ltr />
          <Field label="name_ar" name="tier-ar" value={tierAr} onChange={setTierAr} required />
          <Field label="name_en" name="tier-en" value={tierEn} onChange={setTierEn} required ltr />
          <Field label="rank" name="tier-rank" type="number" value={tierRank} onChange={setTierRank} required ltr />
          {createTier.error ? <ErrorState error={createTier.error} /> : null}
          <button className="btn" type="submit" disabled={createTier.isPending}>
            {t('action.create')}
          </button>
        </form>
      </Card>

      <Card title="sponsors">
        {sponsors.error ? <ErrorState error={sponsors.error} onRetry={() => void sponsors.refetch()} /> : null}
        {sponsors.data ? (
          <DataTable<Sponsor>
            caption="sponsors"
            rows={sponsors.data.items ?? []}
            rowKey={(s) => s.sponsor_id}
            empty={<EmptyState />}
            columns={[
              { key: 'name', header: t('table.name'), cell: (s) => s.sponsor_name ?? <Ltr>{s.organization_id ?? ''}</Ltr> },
              { key: 'tier', header: 'tier_id', cell: (s) => <Ltr>{s.tier_id.slice(0, 8)}</Ltr> },
              {
                key: 'status',
                header: t('table.status'),
                cell: (s) => <Badge tone={statusTone(s.status)}>{s.status}</Badge>,
              },
              { key: 'from', header: 'active_from', cell: (s) => formatDateTime(s.active_from) },
              { key: 'to', header: 'active_to', cell: (s) => formatDateTime(s.active_to) },
              {
                key: 'order',
                header: 'display_order',
                cell: (s) => <span className="numeral">{s.display_order}</span>,
              },
              {
                key: 'actions',
                header: t('table.actions'),
                cell: (s) => (
                  <span className="row">
                    {['activate', 'remove'].map((verb) => (
                      <button
                        key={verb}
                        type="button"
                        className={verb === 'remove' ? 'btn btn--destructive btn--small' : 'btn btn--small'}
                        disabled={sponsorAction.isPending}
                        onClick={() =>
                          sponsorAction.mutate(newIntent({
                            path: `/v1/admin/sponsors/${s.sponsor_id}:${verb}`,
                            body: {},
                            ifMatch: s.version,
                          }))
                        }
                      >
                        {verb}
                      </button>
                    ))}
                  </span>
                ),
              },
            ]}
          />
        ) : null}
        {/* A sponsor transition is FENCED on `s.version`, so its refusals are the fenced family:
            412 needs a re-read (the row in the table is stale), 409 is a decision about the current
            state and needs none. `ErrorState` rendered both as an undifferentiated failure with no
            way forward. */}
        {sponsorAction.error ? (
          <RefusalWithRemedy
            error={sponsorAction.error}
            action="sponsor.manage"
            retryExact={sponsorAction.retryLast}
            onReread={() => void sponsors.refetch()}
          />
        ) : null}

        <form
          onSubmit={(ev) => {
            ev.preventDefault();
            createSponsor.mutate(newIntent({
              path: sponsorsPath,
              body: {
                sponsor_name: sponsorName,
                tier_id: tierId,
                link_url: linkUrl,
                display_order: Number(order),
                active_from: activeFrom ? new Date(activeFrom).toISOString() : null,
                active_to: activeTo ? new Date(activeTo).toISOString() : null,
              },
            }));
          }}
        >
          <Field label="sponsor_name" name="sponsor-name" value={sponsorName} onChange={setSponsorName} required />
          <Select
            label="tier_id"
            name="sponsor-tier"
            value={tierId}
            options={[{ value: '', label: '—' }].concat(
              tierItems.map((x) => ({ value: x.tier_id, label: `${x.code} (${x.rank})` })),
            )}
            onChange={setTierId}
            required
          />
          <Field label="link_url" name="sponsor-link" type="url" value={linkUrl} onChange={setLinkUrl} ltr />
          <Field label="display_order" name="sponsor-order" type="number" value={order} onChange={setOrder} ltr />
          <Field
            label="active_from"
            name="sponsor-from"
            type="datetime-local"
            value={activeFrom}
            onChange={setActiveFrom}
            ltr
          />
          <Field
            label="active_to"
            name="sponsor-to"
            type="datetime-local"
            value={activeTo}
            onChange={setActiveTo}
            ltr
          />
          {createSponsor.error ? <ErrorState error={createSponsor.error} /> : null}
          <button className="btn" type="submit" disabled={createSponsor.isPending}>
            {t('action.create')}
          </button>
        </form>
      </Card>

      <Card title="website strip preview">
        <p className="muted">{t('admin.sponsors.desc')}</p>
        <div className="row">
          {(sponsors.data?.items ?? [])
            .slice()
            .sort((a, b) => a.display_order - b.display_order)
            .map((s) => (
              <Badge key={s.sponsor_id} tone={s.status === 'active' ? 'ok' : 'neutral'}>
                {s.sponsor_name ?? s.sponsor_id.slice(0, 8)}
              </Badge>
            ))}
        </div>
      </Card>
    </>
  );
}
